import { ActionButtonPageTitle } from '@strata/react-buttons';
import { FormHeaderAndContent } from '@strata/react-form-header-and-content';
import { GridWithFilters } from '@strata/react-grid';
import { MessagingBlock } from '@strata/react-messaging-block';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

import { useGetRemainingTransferScriptsQuery } from 'store/api/ndpApi';
import { Grids, useGridState } from 'store/grids/gridsSlice';

const SORT_DEFAULTS = [{ id: 'patientName', desc: false }];

const BACK_LABEL = 'Remaining Transfers';
const TITLE = 'Remaining Transfers';
const SECONDARY_TITLE = 'All Scripts';

const scriptColumns = [
    { accessorKey: 'patientName', header: 'Patient Name', size: 180 },
    { accessorKey: 'drugName', header: 'Product', size: 200 },
    { accessorKey: 'healthPlan', header: 'Payer', size: 180 },
    { accessorKey: 'pharmacyName', header: 'Receiving Pharmacy', size: 180 },
    { accessorKey: 'quantity', header: 'Qty', size: 100 },
    { accessorKey: 'daysSupply', header: 'Days Supply', size: 120 },
    { accessorKey: 'instructions', header: 'Directions', size: 220 },
    { accessorKey: 'source', header: 'Source', size: 160 },
    { accessorKey: 'ineligibilityReason', header: 'Issue', size: 200 },
];

const filterCategories = [
    { key: 'drugName', title: 'Product', showCounts: true },
    { key: 'healthPlan', title: 'Payer', showCounts: true },
    { key: 'pharmacyName', title: 'Receiving Pharmacy', showCounts: true },
    { key: 'source', title: 'Source', showCounts: true },
    { key: 'ineligibilityReason', title: 'Issue', showCounts: true },
];

export const OrtfRemainingTransferScriptsTab = ({ onBack, remainingTransfersId }) => {
    const {
        activeFilters,
        filterPaneWidth,
        search,
        sort,
        updateFilterPaneWidth,
        updateGridActiveFilters,
        updateGridSearch,
        updateGridSort,
    } = useGridState(Grids.ORTF_REMAINING_TRANSFER_SCRIPTS, SORT_DEFAULTS);

    const { data, isError, isFetching } = useGetRemainingTransferScriptsQuery({
        remainingTransfersId,
    });

    const scripts = useMemo(() => data?.scripts ?? [], [data?.scripts]);

    const pageTitle = (
        <ActionButtonPageTitle
            label={BACK_LABEL}
            onClick={onBack}
            secondaryTitle={SECONDARY_TITLE}
            title={TITLE}
        />
    );

    if (isError) {
        return (
            <FormHeaderAndContent
                rowOneContentHeader={pageTitle}
                mainContent={
                    <MessagingBlock
                        className="margin__around--medium"
                        secondaryText="Unable to load remaining transfer scripts."
                        size={MessagingBlock.Sizes.MEDIUM}
                    />
                }
            />
        );
    }

    return (
        <FormHeaderAndContent
            rowOneContentHeader={pageTitle}
            mainContent={
                <GridWithFilters
                    activeFilters={activeFilters}
                    columns={scriptColumns}
                    data={scripts}
                    enableSearch
                    filterCategories={filterCategories}
                    filterPaneRenderWidth={filterPaneWidth}
                    isLoading={isFetching}
                    onFilter={updateGridActiveFilters}
                    onFilterPaneResize={updateFilterPaneWidth}
                    onSearch={updateGridSearch}
                    onSort={updateGridSort}
                    searchText={search}
                    sortProperties={sort}
                />
            }
        />
    );
};

OrtfRemainingTransferScriptsTab.propTypes = {
    onBack: PropTypes.func.isRequired,
    remainingTransfersId: PropTypes.string.isRequired,
};
